import React, { useContext, useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from 'react-i18next';
import { Alert, Button, Collapse, Stack } from '@mui/material';
import { AuthContext } from '../../FirebaseAuth';

// Firebase caches emailVerified/the ID token client-side, so a user who just
// clicked the verification link in another tab won't see it reflected here
// until we reload() the user and force a fresh token. We do that on an
// interval while unverified, on tab focus, and on demand via the button
// below — the fresh token matters because Cloud Functions (createAccount,
// createSubscription) check context.auth.token.email_verified server-side.
const RECHECK_INTERVAL_MS = 10000;

const EmailVerificationBanner = () => {
    const { t } = useTranslation();
    const { authUser } = useContext(AuthContext);
    const user = authUser.user;
    const mountedRef = useRef(true);

    const [verified, setVerified] = useState(user ? user.emailVerified : true);
    const [sendStatus, setSendStatus] = useState('idle'); // idle | sending | sent | error
    const [checking, setChecking] = useState(false);

    useEffect(() => {
        return () => { mountedRef.current = false };
    }, []);

    useEffect(() => {
        setVerified(user ? user.emailVerified : true);
    }, [user]);

    const recheckVerification = useCallback(() => {
        if (!user || user.emailVerified) return;
        setChecking(true);
        user.reload().then(() => {
            if (!mountedRef.current) return;
            if (user.emailVerified) {
                // Force-refresh the ID token so the next callable Cloud
                // Function call carries the updated email_verified claim.
                return user.getIdToken(true).then(() => {
                    if (!mountedRef.current) return;
                    setVerified(true);
                    setChecking(false);
                });
            }
            setChecking(false);
        }).catch(() => {
            if (!mountedRef.current) return;
            setChecking(false);
        });
    }, [user]);

    useEffect(() => {
        if (!user || verified) return;
        const onFocus = () => recheckVerification();
        window.addEventListener('focus', onFocus);
        const interval = setInterval(recheckVerification, RECHECK_INTERVAL_MS);
        return () => {
            window.removeEventListener('focus', onFocus);
            clearInterval(interval);
        };
    }, [user, verified, recheckVerification]);

    if (!user || verified) return null;

    const resend = () => {
        setSendStatus('sending');
        user.sendEmailVerification().then(() => {
            if (!mountedRef.current) return;
            setSendStatus('sent');
        }).catch(() => {
            if (!mountedRef.current) return;
            setSendStatus('error');
        });
    };

    return (
        <Collapse in={true}>
            <Alert
                severity="warning"
                sx={{ borderRadius: 0 }}
                action={
                    <Stack direction="row" spacing={1} sx={{ pr: 1, alignItems: 'center' }}>
                        <Button
                            variant="contained"
                            color="warning"
                            size="small"
                            onClick={resend}
                            disabled={sendStatus === 'sending'}
                            sx={{ whiteSpace: 'nowrap' }}
                        >
                            {sendStatus === 'sent' ? t('emailVerificationBanner.emailSent') : t('emailVerificationBanner.resend')}
                        </Button>
                        <Button
                            variant="outlined"
                            color="warning"
                            size="small"
                            onClick={recheckVerification}
                            disabled={checking}
                            sx={{ whiteSpace: 'nowrap' }}
                        >
                            {checking ? t('emailVerificationBanner.checking') : t('emailVerificationBanner.iveVerified')}
                        </Button>
                    </Stack>
                }
            >
                {sendStatus === 'error'
                    ? t('emailVerificationBanner.sendError')
                    : t('emailVerificationBanner.message', { email: user.email })}
            </Alert>
        </Collapse>
    );
}

export default EmailVerificationBanner;
